import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import CollaboratorLayout from "@/components/CollaboratorLayout";

import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import PaymentReturn from "@/pages/PaymentReturn";

import CollaboratorSheet from "@/pages/CollaboratorSheet";
import CollaboratorStorico from "@/pages/CollaboratorStorico";
import Versamento from "@/pages/Versamento";
import ContoSeparato from "@/pages/ContoSeparato";

import AdminDashboard from "@/pages/admin/Dashboard";
import Collaboratori from "@/pages/admin/Collaboratori";
import Impostazioni from "@/pages/admin/Impostazioni";
import Storico from "@/pages/admin/Storico";
import SheetDetail from "@/pages/admin/SheetDetail";
import Abbonamento from "@/pages/admin/Abbonamento";
import FlussoCassa from "@/pages/admin/FlussoCassa";
import Aiuto from "@/pages/Aiuto";
import CambioPassword from "@/pages/CambioPassword";
import StoricoVersamenti from "@/pages/StoricoVersamenti";
import Privacy from "@/pages/legal/Privacy";
import Termini from "@/pages/legal/Termini";
import Sospesi from "@/pages/Sospesi";
import ControlloCC from "@/pages/ControlloCC";
import DaVersare from "@/pages/admin/DaVersare";
import AbbonamentoBloccato from "@/pages/AbbonamentoBloccato";

import MasterLayout from "@/components/MasterLayout";
import MasterLogin from "@/pages/master/MasterLogin";
import MasterDashboard from "@/pages/master/MasterDashboard";
import MasterAgenzie from "@/pages/master/MasterAgenzie";
import MasterAudit from "@/pages/master/MasterAudit";
import MasterRichieste from "@/pages/master/MasterRichieste";
import MasterPagamenti from "@/pages/master/MasterPagamenti";
import MasterComunicazioni from "@/pages/master/MasterComunicazioni";

const ADMIN_ROLES = ["agente", "consultazione"];

function AppRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  if (user.must_change_password) return <Navigate to="/cambio-password" replace />;
  if (user.role === "platform_admin") return <Navigate to="/master" replace />;
  if (user.role === "agente" || user.role === "consultazione") return <Navigate to="/admin" replace />;
  if (user.role === "collaboratore" || user.role === "interno") return <Navigate to="/foglio-cassa" replace />;
  return <Navigate to="/login" replace />;
}

function CambioPasswordGuard() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <CambioPassword />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<AppRedirect />} />
          <Route path="/registrazione" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/pagamento-completato" element={<PaymentReturn />} />
          <Route path="/abbonamento-bloccato" element={<AbbonamentoBloccato />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/termini" element={<Termini />} />

          {/* Forced password change after reset — authenticated but not blocked by guard */}
          <Route path="/cambio-password" element={<CambioPasswordGuard />} />

          {/* Collaborator/Internal area */}
          <Route
            element={
              <ProtectedRoute roles={["collaboratore", "interno"]}>
                <CollaboratorLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/foglio-cassa" element={<CollaboratorSheet />} />
            <Route path="/foglio-cassa/:id" element={<CollaboratorSheet />} />
            <Route
              path="/storico"
              element={
                <ProtectedRoute role="collaboratore">
                  <CollaboratorStorico />
                </ProtectedRoute>
              }
            />
            <Route
              path="/versamento"
              element={
                <ProtectedRoute role="collaboratore">
                  <Versamento />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conto-separato"
              element={
                <ProtectedRoute role="collaboratore">
                  <ContoSeparato />
                </ProtectedRoute>
              }
            />
            <Route path="/aiuto" element={<Aiuto />} />
            <Route path="/storico-versamenti" element={<StoricoVersamenti />} />
            <Route path="/sospesi" element={<Sospesi />} />
            <Route path="/controllo-cc" element={<ControlloCC />} />
          </Route>

          {/* Agente area (admin alias too for legacy URL paths) */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={ADMIN_ROLES}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="collaboratori" element={<Collaboratori />} />
            <Route path="impostazioni" element={<Impostazioni />} />
            <Route path="storico" element={<Storico />} />
            <Route path="storico-versamenti" element={<StoricoVersamenti />} />
            <Route path="foglio-cassa/:id" element={<SheetDetail />} />
            <Route path="foglio-cassa-agenzia" element={<CollaboratorSheet />} />
            <Route path="versamento" element={<Versamento />} />
            <Route path="flusso-cassa" element={<FlussoCassa />} />
            <Route path="abbonamento" element={<Abbonamento />} />
            <Route path="aiuto" element={<Aiuto />} />
            <Route path="sospesi" element={<Sospesi />} />
            <Route path="controllo-cc" element={<ControlloCC />} />
            <Route path="da-versare" element={<DaVersare />} />
          </Route>

          {/* Master area */}
          <Route path="/master/login" element={<MasterLogin />} />
          <Route path="/master" element={<MasterLayout />}>
            <Route index element={<MasterDashboard />} />
            <Route path="agenzie" element={<MasterAgenzie />} />
            <Route path="pagamenti" element={<MasterPagamenti />} />
            <Route path="richieste" element={<MasterRichieste />} />
            <Route path="comunicazioni" element={<MasterComunicazioni />} />
            <Route path="audit" element={<MasterAudit />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
